import { Component, OnDestroy, OnInit } from '@angular/core';
import { Logger } from '../../services/logger.service';
import { BehaviorSubject, Subscription } from 'rxjs';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import {
  UserContextEnum,
  UserContextService,
} from '../../services/user-context.service';

interface LayoutConfig {
  pattern: RegExp;
  hideHeadbar: boolean;
  hideNavigation: boolean;
}

@Component({
  selector: 'app-main-layout',
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.scss'],
})
export class MainLayoutComponent implements OnInit, OnDestroy {
  private readonly logger = new Logger(MainLayoutComponent.name);

  isHeadbarVisible = new BehaviorSubject(true);
  isNavigationVisible = new BehaviorSubject(true);
  userContextEnum = UserContextEnum;

  private routerSubscription?: Subscription;
  private userContextSubscription?: Subscription;
  private isNavigationLoadingSubject = new BehaviorSubject(false);
  isNavigationLoading$ = this.isNavigationLoadingSubject.asObservable();

  private readonly layoutConfigs: LayoutConfig[] = [
    {
      pattern: /^\/auth.*/,
      hideHeadbar: true,
      hideNavigation: true,
    },
    {
      pattern: /^\/coach\/create/,
      hideHeadbar: false,
      hideNavigation: true,
    },
    {
      pattern: /^\/coachee\/create/,
      hideHeadbar: false,
      hideNavigation: true,
    },
    {
      pattern: /^\/error/,
      hideHeadbar: true,
      hideNavigation: true,
    },
    {
      pattern: /^\/map/,
      hideHeadbar: true,
      hideNavigation: false,
    },
    {
      pattern: /^\/notifications/,
      hideHeadbar: true,
      hideNavigation: false,
    },
    {
      pattern: /^\/home/,
      hideHeadbar: true,
      hideNavigation: false,
    },
    {
      pattern: /^\/profile\/[0-9]+/,
      hideHeadbar: true,
      hideNavigation: false,
    },
  ];

  constructor(
    private router: Router,
    private userContextService: UserContextService
  ) {}

  ngOnInit(): void {
    // Initial check
    this.updateLayoutVisibility(this.router.url);

    // Apply user context class to body
    this.updateBodyClass(this.userContext);

    // Subscribe to user context changes
    this.userContextSubscription =
      this.userContextService.userContext$.subscribe({
        next: context => {
          this.updateBodyClass(context);
        },
        error: error => {
          this.logger.error('Error in user context subscription', error);
        },
      });

    // Subscribe to route changes
    this.routerSubscription = this.router.events.subscribe({
      next: (event: any) => {
        if (event instanceof NavigationStart) {
          this.isNavigationLoadingSubject.next(true);
          return;
        }

        if (
          event instanceof NavigationEnd ||
          event instanceof NavigationError ||
          event instanceof NavigationCancel
        ) {
          this.updateLayoutVisibility(event.url);
          this.isNavigationLoadingSubject.next(false);
        }
      },
      error: error => {
        this.logger.error('Error in router subscription', error);
      },
    });
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
    this.userContextSubscription?.unsubscribe();
  }

  private updateLayoutVisibility(url: string): void {
    this.logger.debug('Layout visibility update for url', url);

    // Find matching configuration
    const matchingConfig = this.layoutConfigs.find(config =>
      config.pattern.test(url)
    );

    // If no matching config is found, show both
    if (!matchingConfig) {
      this.isHeadbarVisible.next(true);
      this.isNavigationVisible.next(true);
      return;
    }

    // Apply configuration
    this.isHeadbarVisible.next(!matchingConfig.hideHeadbar);
    this.isNavigationVisible.next(!matchingConfig.hideNavigation);
  }

  get userContext(): UserContextEnum {
    return this.userContextService.getCurrentUserContext();
  }

  private updateBodyClass(context: UserContextEnum): void {
    const bodyElement = document.body;

    // Remove all context classes first
    bodyElement.classList.remove('coach', 'coachee');

    // Add the appropriate class based on context
    if (context === UserContextEnum.Coach) {
      bodyElement.classList.add('coach');
    } else if (context === UserContextEnum.Coachee) {
      bodyElement.classList.add('coachee');
    }
  }
}
